import { observer } from 'mobx-react';
import { useEffect, useMemo } from 'react';
import branchEditStore from '../../store/branchEditStore';
import { defaultIntegrationTooltipOptions } from 'src/constants/integrations';
import { LOCALES } from 'src/constants/main';
import { countriesStore } from 'src/mobx/countriesStore';
import integrationsStore from 'src/mobx/integrationStore';
import { localeStore } from 'src/mobx/localesStore';
import { isDisabledByPos } from 'src/utils/helpers';
import Input from 'src/view/components/Controls/Input/Input';
import MultiLanguageInput from 'src/view/components/Controls/MultiLanguageInput/MultiLanguageInput';
import PhoneInput from 'src/view/components/Controls/PhoneInput/PhoneInput';
import IntegrationManager from 'src/view/components/IntegrationManager/IntegrationManager';
import IntegrationTooltip from 'src/view/components/IntegrationTooltip/IntegrationTooltip';
import Text, { TEXT } from 'src/view/components/Text/Text';
import { isFranchiseDisableByPos } from '~/helpers';
import './style.scss';
import { appStore } from '~/mobx/appStore';

const MainDetails = observer((): JSX.Element => {
  useEffect(() => {
    countriesStore.fetchCountries();
  }, []);

  const onChangeName = (value: string, lang: LOCALES) => {
    branchEditStore.changeBranchData({ name: { ...branchEditStore.editableBranch.name, [lang]: value } });
  };

  const onChangePhoneNumber = (value: string) => {
    branchEditStore.changeBranchData({ phone: value });
  };

  const onChangeExternalId = (value: string) => {
    branchEditStore.changeBranchData({ external_id: value });
  };

  const { branchNames: branchNameRestrictionAccess, branchPhoneNumber: branchPhoneRestrictionAccess } = integrationsStore.posRestrictions || {};

  const isBranchPhoneDisabledByPos = isDisabledByPos(branchPhoneRestrictionAccess);
  const isBranchNameDisabledByPos = useMemo(() => {
    if (isFranchiseDisableByPos(branchEditStore.editableBranch.id, branchEditStore.editableBranch.created_in)) {
      return false;
    }

    return isDisabledByPos(branchNameRestrictionAccess);
  }, [branchEditStore.editableBranch.id, branchNameRestrictionAccess, appStore.isAddonsAvailable.franchise]);

  return (
    <div className="branch-main-details">
      <Text type={TEXT.SECTION_HEADER} text="main_details" className="mb-4" />

      {/* branch name */}
      <IntegrationManager setting={branchNameRestrictionAccess} wrapperClassName="d-flex flex-column mb-5">
        <Text type={TEXT.BLOCK_HEADER} text="branch_name" className="mb-2" disabled={isBranchNameDisabledByPos} required />
        <MultiLanguageInput
          enText={branchEditStore.editableBranch.name.en}
          arText={branchEditStore.editableBranch.name.ar}
          validation={branchEditStore.validation.details.name}
          tooltip={isBranchNameDisabledByPos ? defaultIntegrationTooltipOptions : undefined}
          disabled={isBranchNameDisabledByPos}
          placeholder={localeStore.t('type_in_branch_name')}
          onChangeValue={onChangeName}
        />
      </IntegrationManager>

      {/* phone number */}
      <IntegrationManager setting={branchPhoneRestrictionAccess} wrapperClassName="d-flex flex-column mb-5">
        <Text type={TEXT.BLOCK_HEADER} text="branch_phone_number" className="mb-2" disabled={isBranchPhoneDisabledByPos} required />
        <IntegrationTooltip isVisible={isBranchPhoneDisabledByPos}>
          <PhoneInput
            value={branchEditStore.editableBranch.phone}
            country={countriesStore.defaultCountryCode}
            onlyCountries={countriesStore.countriesForPhoneInput}
            error={!branchEditStore.validation.details.phone.isValid}
            errorText={branchEditStore.validation.details.phone.errorText}
            onChange={onChangePhoneNumber}
          />
        </IntegrationTooltip>
      </IntegrationManager>

      {/* external id */}
      <div className="d-flex flex-column">
        <Text type={TEXT.BLOCK_HEADER} text="external_id" className="mb-2" />
        <Input
          value={branchEditStore.editableBranch.external_id || ''}
          placeholder={localeStore.t('Value')}
          className="input input-mini w-100 family-medium text-size-16"
          onChangeText={onChangeExternalId}
        />
      </div>
    </div>
  );
});

export default MainDetails;
